import { Component, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
}
interface State {
  hasChunkError: boolean;
}

const RELOAD_KEY = "__chunk_reload__";

function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Failed to load module script/i.test(msg) ||
    /ChunkLoadError/i.test(msg)
  );
}

export default class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasChunkError: false };

  static getDerivedStateFromError(err: unknown): State | null {
    if (isChunkLoadError(err)) return { hasChunkError: true };
    return null;
  }

  componentDidCatch(err: unknown) {
    if (!isChunkLoadError(err)) return;
    // Attempt a single auto-reload to pick up the latest index.html + chunk hashes
    try {
      const reloaded = sessionStorage.getItem(RELOAD_KEY);
      if (!reloaded) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
      }
    } catch {
      // ignore storage errors
    }
  }

  handleManualReload = () => {
    try {
      sessionStorage.removeItem(RELOAD_KEY);
    } catch {
      // ignore
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasChunkError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6">
          <div className="max-w-md text-center space-y-4">
            <h2 className="text-xl font-semibold text-foreground">มีเวอร์ชันใหม่ของแอป</h2>
            <p className="text-sm text-muted-foreground">
              กำลังโหลดเวอร์ชันล่าสุด ถ้าหน้านี้ค้าง กดปุ่มด้านล่างเพื่อรีโหลด
            </p>
            <Button onClick={this.handleManualReload}>
              <RefreshCw className="h-4 w-4 mr-2" />
              รีโหลดหน้านี้
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
